import * as yup from 'yup';

import { ChangeEvent, FC, useCallback, useState } from 'react';

import { CreditCardType, useUpdateCard } from '@/logic/services';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useSnack } from '@/logic/state';

import shallow from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { yupResolver } from '@hookform/resolvers/yup';
import { CloseButton } from '../shared/close-button';
import { DialogFormTitle } from '../shared/dialog-form/dialog-form-title';
import { CreditCardNameInput } from '../shared/add-credit-card/credit-card-name-input';
import { CreditCardSwitchInput } from '../shared/add-credit-card/credit-card-switch-input';
import { DialogFormButton } from '../shared/dialog-form/dialog-form-button';

interface Form {
  cardName: string;
  cardHolder: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
}

export const EditCreditCardForm: FC = () => {
  const theme = useTheme();
  const intl = useIntl();

  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const updateCard = useUpdateCard();
  const router = useRouter();
  const schema = yup.object({
    cardName: yup
      .string()
      .required()
      .max(40, intl.formatMessage({ id: 'my_credit_cards.card_name.validation.fail' })),
    cardHolder: yup.string().required(),
    cardNumber: yup.string().required(),
    expiryDate: yup.string().required(),
    cvv: yup.string().required(),
  });

  const { id, isDefault, cardType, ...defaultValues } = dialog?.editCreditCardFormPayload;

  const [isCardDefault, setIsCardDefault] = useState(isDefault);

  const queryClient = useQueryClient();

  const {
    register,
    getValues,
    formState: { errors, isValid },
  } = useForm<Form>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultValues,
      cardNumber: `**** ${defaultValues.cardNumber}`,
      cvv: cardType === CreditCardType.Amex ? '****' : '***',
    },
  });

  const handleCloseClick = useCallback(() => {
    setDialog({});
  }, [setDialog]);

  const handleFormSwitchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsCardDefault(e.target.checked);
  };

  const handleSubmit = useCallback(() => {
    updateCard.safeMutateAsync(
      {
        data: {
          name: getValues('cardName'),
          isDefault: isCardDefault,
        },
        query: {
          id,
        },
      },
      {
        onSuccess: () => {
          setSnack({
            isOpen: true,
            message: 'my_credit_cards.update.success',
            type: 'success',
            id: 'credit-card-updated-success',
          });
          setDialog({});
        },
        onSettled: () =>
          queryClient.invalidateQueries([
            {
              method: 'get',
              url: 'cards',
              params: {
                limit: 50,
                page: 1,
              },
            },
          ]),
      }
    );
  }, [getValues, id, isCardDefault, queryClient, setDialog, setSnack, updateCard]);

  return (
    <>
      <DialogTitle>
        <CloseButton handleClick={handleCloseClick} />
      </DialogTitle>
      <DialogContent
        sx={{
          textAlign: 'center',
        }}
      >
        <DialogFormTitle message='my_credit_cards.edit_credit_card' />
        <Stack spacing='24px'>
          <CreditCardNameInput
            isLoading={updateCard.isLoading}
            isAutoFocus={false}
            isError={errors.cardName?.type && errors.cardName?.type !== 'required'}
            helperText={
              errors.cardName?.type && errors.cardName?.type !== 'required'
                ? errors.cardName?.message
                : ''
            }
            registerFunc={() => register('cardName')}
          />

          <TextField
            id='credit-card-number'
            label={<FormattedMessage id='my_credit_cards.card_number' />}
            disabled
            InputProps={{
              sx: {
                color: theme.palette.grey[80],
                '& input': {
                  textAlign: router.locale === 'en' ? 'start' : 'end',
                },
              },
            }}
            {...register('cardNumber')}
            dir='ltr'
          />

          <TextField
            id='credit-card-holder'
            label={<FormattedMessage id='my_credit_cards.card_holder' />}
            disabled
            InputProps={{
              sx: {
                color: theme.palette.grey[80],
              },
            }}
            {...register('cardHolder')}
          />

          <TextField
            id='credit-card-expiry-date'
            label={<FormattedMessage id='my_credit_cards.expiry_date' />}
            disabled
            InputProps={{
              sx: {
                color: theme.palette.grey[80],
              },
            }}
            {...register('expiryDate')}
          />

          <TextField
            id='credit-card-cvv'
            label={<FormattedMessage id='my_credit_cards.cvv' />}
            disabled
            InputProps={{
              sx: {
                color: theme.palette.grey[80],
              },
            }}
            {...register('cvv')}
          />
        </Stack>
        <CreditCardSwitchInput
          isLoading={updateCard.isLoading}
          isChecked={isCardDefault}
          label='my_credit_cards.set_card_default'
          handleChange={handleFormSwitchChange}
        />
      </DialogContent>
      <DialogActions>
        <DialogFormButton
          isLoading={updateCard.isLoading}
          isDisabled={!isValid}
          label='global.save'
          handleSave={handleSubmit}
        />
      </DialogActions>
    </>
  );
};
